import React, {
	useEffect,
	useState,
	useContext,
	useMemo,
	useCallback,
} from 'react';
import { OrderContext } from '../../../../context/OrderContext/order.context';
import { useGetCategories } from '../../../../hooks/use-get-categories';
import { CompanyService } from '../../../../services/company.services';
import { GeoService } from '../../../../services/geo.services';
import Modal from 'react-modal';
import { SypacButton, SypacText } from '@sypac/component-library-react';
import { T } from '@tolgee/react';
import Close from '../../../../assets/Close';
import { AdminContracts } from '../../../../assets/Contracts/AdminContracts';
import SimpleOrderCart from '../../../../assets/SimpleOrderCart';
import { cn } from '../../../../lib/utils';
import { NumericFormat } from 'react-number-format';
import { SelectOrderTypeProps } from '../clientSelectLocation.interface';
import ContractStatusBadge from '../../../../components/ContractsTable/components/ContractStatusBadge';
import Flag from 'react-world-flags';
import DashedDivider from '../../../../components/DashedDivider/DashedDivider';

const SelectOrderType: React.FC<SelectOrderTypeProps> = ({
	isOpen,
	onClose,
	onSave,
	contracts,
}) => {
	const language = localStorage.getItem('lang') || 'en';
	const [step, setStep] = useState<'select_type' | 'contract_order'>(
		'select_type',
	);
	const [selectedType, setSelectedType] = useState<
		'simple' | 'contract' | null
	>(null);
	const { order } = useContext(OrderContext);
	const [userCountry, setUserCountry] = useState<string>();
	const [coordinates, setCoordinates] = useState<number[]>([]);

	const getMyCompany = useCallback(async () => {
		try {
			const { data } = await CompanyService.getMeCompany();
			const map3: Record<string, string> = { MD: 'MDA', PL: 'POL' };

			setUserCountry(map3[data.countryCode] ?? '');
		} catch {}
	}, []);

	const getLocationCoords = useCallback(async (locationId?: string) => {
		if (!locationId) return;
		try {
			const { data } = await GeoService.getLocationDetails({
				locationid: locationId,
			});
			setCoordinates([data.Longitude, data.Latitude]);
		} catch {}
	}, []);

	useEffect(() => {
		if (!isOpen) return;
		getMyCompany().then(() => {});
		getLocationCoords(order?.location?.locationId).then(() => {});
	}, [isOpen, getMyCompany, getLocationCoords, order?.location?.locationId]);

	const categoryIds = useMemo(() => {
		const ids = new Set<string>();
		contracts?.forEach((c) =>
			c?.lines?.forEach((l) => {
				const cid = l?.product?.categoryId ?? l?.product?.category?.id;
				if (cid) ids.add(String(cid));
			}),
		);
		return Array.from(ids);
	}, [contracts]);

	const [categoriesResp] = useGetCategories({
		ids: categoryIds,
		coordinates,
		range: 500,
		countryCode: userCountry,
	});

	const categoryToGroupId = useMemo(() => {
		const map = new Map<string, string>();
		categoriesResp?.items?.forEach((c) => {
			if (c?.id && c?.parentId) map.set(String(c.id), String(c.parentId));
		});
		return map;
	}, [categoriesResp]);

	useEffect(() => {
		if (!isOpen) {
			setStep('select_type');
			setSelectedType(null);
		}
	}, [isOpen]);

	return (
		<Modal
			isOpen={isOpen}
			onRequestClose={onClose}
			className={cn(
				'modal-inside outline-none min-w-[556px] box-border',
				step === 'contract_order' && 'h-[583px]',
			)}
			overlayClassName="modal-overlay"
			shouldCloseOnOverlayClick={false}
			ariaHideApp={false}
		>
			<div className="h-full flex flex-col">
				<div className="flex flex-col gap-3 p-3">
					<div className="flex items-center justify-between">
						<SypacText variant="body-regular-medium">
							<p className="text-gray-90">
								{step === 'contract_order' ? (
									<T keyName="selectOrderType.selectContract">
										Select contract
									</T>
								) : (
									<T keyName="selectOrderType.selectOrderType">
										Select order type
									</T>
								)}
							</p>
						</SypacText>

						<SypacButton variant="subTitle">
							<button
								type="button"
								className="flex justify-center items-center h-[32px] w-[32px] p-0 bg-white border border-solid border-gray-10 transition hover:border-gray-60 rounded-lg"
								onClick={onClose}
							>
								<Close width="10" height="10" />
							</button>
						</SypacButton>
					</div>
				</div>
				<div className="border-0 border-solid border-t-[1px] border-gray-10" />

				{step === 'contract_order' ? (
					<div className="flex flex-col gap-3 p-3 overflow-y-scroll">
						{contracts?.map((contract) =>
							contract.lines?.map((line) => (
								<button
									key={line.id}
									className="w-full h-fit flex flex-col gap-3 p-0 items-center bg-transparent border border-solid border-gray-10 rounded-10 transition hover:border-primary-violet hover:shadow-md cursor-pointer"
									onClick={() => {
										const productId = line?.product?.id ?? line?.productId;

										const categoryId = line?.product?.categoryId!;

										const groupId = categoryToGroupId.get(String(categoryId));

										onSave({
											kind: 'contract',
											productId,
											categoryId,
											groupId: groupId!,
											contract,
										});
									}}
								>
									<div className="w-full flex items-center justify-between px-[10px] pt-[10px] box-border">
										<SypacText variant="body-regular-medium">
											<p className="text-gray-80 text-[14px] leading-[16px]">
												<T keyName="selectOrderType.contractId">Contract ID:</T>{' '}
												{contract.id}
											</p>
										</SypacText>

										<ContractStatusBadge status={contract.status} />
									</div>
									<div className="w-full border-0 border-solid border-t-[1px] border-gray-10" />

									<div className="w-full flex flex-col gap-[10px] justify-center px-[10px] pb-[10px] box-border">
										<div className="h-[16px] grid grid-cols-[220px,auto] gap-5 self-start">
											<SypacText variant="body-regular-medium">
												<p className="text-gray-40 text-[14px] leading-[16px] text-start">
													<T keyName="selectOrderType.productName">
														Product name:
													</T>
												</p>
											</SypacText>

											<SypacText variant="body-regular-medium">
												<p className="text-gray-80 text-[14px] leading-[16px]">
													{line.product?.translations?.find(
														(locale) => locale.language === language,
													)?.name || line.product?.name}
												</p>
											</SypacText>
										</div>

										{(line?.product?.type || line?.product?.size) && (
											<>
												<div className="h-[16px] grid grid-cols-[220px,auto] gap-5 self-start">
													<SypacText variant="body-regular-medium">
														<p className="text-gray-40 text-[14px] leading-[16px] text-start">
															<T keyName="selectOrderType.productType">
																Product type:
															</T>
														</p>
													</SypacText>

													<SypacText variant="body-regular-medium">
														<p className="text-gray-80 text-[14px] leading-[16px]">
															{line?.product?.type}{' '}
															{line?.product?.size &&
																`(${line?.product?.size})`}
														</p>
													</SypacText>
												</div>
											</>
										)}

										{line.store && (
											<>
												<div className="h-[16px] grid grid-cols-[220px,auto] gap-5 self-start items-center">
													<SypacText
														variant="body-regular-medium"
														className="col-start-2 min-w-0"
													>
														<p className="text-gray-80 text-[14px] leading-[16px] truncate">
															{line.store.name}
														</p>
													</SypacText>
												</div>

												<div className="h-[16px] grid grid-cols-[220px,auto] gap-5 self-start items-center">
													<div className="flex items-center gap-1">
														{line.store.countryCode ? (
															<span className="mt-[2px]">
																<Flag
																	className="object-cover border border-solid border-gray-10 rounded"
																	code={line.store.countryCode}
																	width={20}
																	height={14}
																/>
															</span>
														) : null}
														<SypacText variant="body-regular-medium">
															<p className="w-full max-w-[100px] text-gray-40 text-[14px] leading-[16px] whitespace-nowrap truncate">
																{line.store.address || ''}
															</p>
														</SypacText>
													</div>
												</div>
											</>
										)}

										<div className="h-[16px] grid grid-cols-[220px,auto] gap-5 self-start">
											<SypacText variant="body-regular-medium">
												<p className="text-gray-40 text-[14px] leading-[16px] text-start">
													<T keyName="selectOrderType.contractQuantity">
														Contract quantity:
													</T>
												</p>
											</SypacText>

											<SypacText variant="body-regular-medium">
												<p className="text-gray-80 text-[14px] leading-[16px]">
													<NumericFormat
														type="text"
														displayType="text"
														thousandSeparator=" "
														decimalSeparator="."
														value={line.contractQtyTons}
													/>{' '}
													<T keyName="basicDetails.tons">t</T>
												</p>
											</SypacText>
										</div>

										<DashedDivider dashLength={5} gapLength={5} />

										<div className="h-[16px] grid grid-cols-[220px,auto] gap-5 self-start">
											<SypacText variant="body-regular-medium">
												<p className="text-gray-40 text-[14px] leading-[16px] text-start">
													<T keyName="selectOrderType.price">Price:</T>
												</p>
											</SypacText>

											<SypacText variant="body-regular-medium">
												<p className="text-gray-80 text-[14px] leading-[16px]">
													<NumericFormat
														type="text"
														displayType="text"
														thousandSeparator=" "
														decimalSeparator="."
														decimalScale={2}
														value={
															line.pricePerTonBuyer * (1 + line.markup / 100) ||
															0
														}
														suffix=" PLN"
													/>
												</p>
											</SypacText>
										</div>
									</div>
								</button>
							)),
						)}
					</div>
				) : (
					<div className="flex flex-col gap-3 p-3">
						{contracts.length ? (
							<div
								className={cn(
									'flex items-start gap-3 p-[10px] bg-[#FAFAFA] border rounded-10 cursor-pointer hover:border-solid hover:border-gray-20',
									selectedType === 'contract'
										? 'border-solid border-gray-20/50'
										: 'border-dashed border-gray-10',
								)}
								onClick={() => setSelectedType('contract')}
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										setSelectedType('contract');
									}
								}}
								role="checkbox"
								aria-checked={selectedType === 'contract'}
								tabIndex={0}
							>
								<input
									type="checkbox"
									name="order_type"
									checked={selectedType === 'contract'}
									onChange={() => setSelectedType('contract')}
									className="
									h-[18px] w-[18px]
									appearance-none
									rounded-[4px]
									border border-solid border-[#D1D1D1]
									bg-white
									m-0 p-0
									cursor-pointer
									transition-colors
									bg-no-repeat bg-center bg-[length:16px_16px]
									checked:bg-[#22C55E] checked:border-[#22C55E]
									checked:bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSI+PHBhdGggZD0iTTQgOC41TDcgMTEuNUwxMiA1IiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Rva2UtbGluZWpvaW49InJvdW5kIi8+PC9zdmc+')]
									outline-none focus:outline-none focus:ring-0 ring-0
								"
								/>
								<div className="flex gap-[14px] items-start">
									<div className="w-[42px] h-[42px] flex items-center justify-center bg-white border border-solid border-gray-10 rounded-[8px]">
										<AdminContracts color="#454545" />
									</div>
									<div className="flex flex-col gap-1">
										<SypacText variant="body-regular-medium">
											<p className="text-gray-80 text-[16px] leading-[24px]">
												<T keyName="selectOrderType.contractOrder">
													Contract order
												</T>
											</p>
										</SypacText>
										{/*
											<SypacText variant="body-regular-medium">
												<p className="text-gray-40 text-[12px] leading-[14px]">
													Lorem ipsum dolor sit amet.
												</p>
											</SypacText>
										*/}
									</div>
								</div>
							</div>
						) : null}
						<div
							className={cn(
								'flex items-start gap-3 p-[10px] bg-[#FAFAFA] border rounded-10 cursor-pointer hover:border-solid hover:border-gray-20',
								selectedType === 'simple'
									? 'border-solid border-gray-20/50'
									: 'border-dashed border-gray-10',
							)}
							onClick={() => setSelectedType('simple')}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									setSelectedType('simple');
								}
							}}
							role="checkbox"
							aria-checked={selectedType === 'simple'}
							tabIndex={0}
						>
							<input
								type="checkbox"
								name="order_type"
								checked={selectedType === 'simple'}
								onChange={() => setSelectedType('simple')}
								className="
									h-[18px] w-[18px]
									appearance-none
									rounded-[4px]
									border border-solid border-[#D1D1D1]
									bg-white
									m-0 p-0
									cursor-pointer
									transition-colors
									bg-no-repeat bg-center bg-[length:16px_16px]
								 checked:bg-[#22C55E] checked:border-[#22C55E]
									checked:bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSI+PHBhdGggZD0iTTQgOC41TDcgMTEuNUwxMiA1IiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Rva2UtbGluZWpvaW49InJvdW5kIi8+PC9zdmc+')]
									outline-none focus:outline-none focus:ring-0 ring-0
								"
							/>
							<div className="flex gap-[14px] items-start">
								<div className="w-[42px] h-[42px] flex items-center justify-center bg-white border border-solid border-gray-10 rounded-[8px]">
									<SimpleOrderCart color="#454545" />
								</div>
								<div className="flex flex-col gap-1">
									<SypacText variant="body-regular-medium">
										<p className="text-gray-80 text-[16px] leading-[24px]">
											<T keyName="selectOrderType.simpleOrderFromStore">
												Simple order from store
											</T>
										</p>
									</SypacText>
									{/*
										<SypacText variant="body-regular-medium">
											<p className="text-gray-40 text-[12px] leading-[14px]">
												Lorem ipsum dolor sit amet.
											</p>
										</SypacText>
									*/}
								</div>
							</div>
						</div>
					</div>
				)}

				<div
					className={cn(
						'flex w-full py-2.5 xl-2xl:py-3 gap-3 [&_button]:transition border border-solid border-b-0 rounded-10 border-gray-10 -mx-[1px] mt-auto',
						contracts.length > 2 && 'shadow-[0_-20px_56px_-24px_#00000052]',
					)}
				>
					<SypacButton
						variant="secondary"
						size="small"
						className="w-full pl-2.5 xl-2xl:pl-3"
					>
						<button
							type="button"
							className="w-full py-2.5 rounded-lg"
							onClick={onClose}
						>
							<SypacText variant="body-regular-medium">
								<p>
									<T keyName="modalChangeRequest.cancel">Cancel</T>
								</p>
							</SypacText>
						</button>
					</SypacButton>

					<SypacButton
						variant="primary"
						size="small"
						className="w-full pr-2.5 xl-2xl:pr-3"
					>
						<button
							type="button"
							className={`w-full py-2.75 rounded-lg ${
								!selectedType && step === 'select_type'
									? 'bg-primary-violet/80 text-gray-500'
									: ''
							}`}
							disabled={!selectedType && step === 'select_type'}
							onClick={() => {
								if (step === 'select_type') {
									if (selectedType === 'simple') {
										onSave({ kind: 'simple' });
									} else if (selectedType === 'contract') {
										setStep('contract_order');
									}
								}
							}}
						>
							<SypacText variant="body-regular-medium">
								<p className="flex text-white gap-3">
									<T keyName="assignAccountManager.confirm">Confirm</T>
								</p>
							</SypacText>
						</button>
					</SypacButton>
				</div>
			</div>
		</Modal>
	);
};

export default SelectOrderType;
